/**
 * Privacy Policy - The Scorekeeper
 * Effective May 12, 2026.
 */
import { Link } from 'react-router-dom';
import { paths } from '../lib/paths';

export function PrivacyPolicyPage() {
  return (
    <article className="m-article">
      <div className="eyebrow">Legal</div>
      <h1 className="h1">Privacy Policy</h1>
      <p className="lede">Effective date: May 12, 2026</p>

      <p>
        The Scorekeeper ("we," "us," or "our") operates thescorekeeper.ai and
        provides software for roller hockey league and tournament management.
        This Privacy Policy explains what information we collect, how we use it,
        when we share it, and the choices available to you.
      </p>

      <p>
        If you use The Scorekeeper through a league, tournament, team, rink, or
        other organization, that organization may also have its own privacy
        practices. League and tournament operators are responsible for the
        information they enter, publish, message, or otherwise manage through
        the platform.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>1. Information we collect</h2>
      <p>We collect information in the following ways.</p>

      <h3 style={{ marginTop: 20, fontSize: '1rem', fontWeight: 700 }}>Account and sign-in information</h3>
      <p>
        When you sign in with Google, we receive information needed to
        authenticate your account, such as your verified email address. We do
        not receive or store your Google password. We use a signed session
        cookie to keep you logged in and to determine whether your account is
        authorized for league administration.
      </p>

      <h3 style={{ marginTop: 20, fontSize: '1rem', fontWeight: 700 }}>Contact and demo requests</h3>
      <p>
        If you contact us, request a demo, or send us feedback, we collect the
        information you provide, such as your name, email address, organization,
        and message.
      </p>

      <h3 style={{ marginTop: 20, fontSize: '1rem', fontWeight: 700 }}>League, team, and participant information</h3>
      <p>
        League operators, admins, captains, scorekeepers, or other authorized
        users may enter information needed to run a league or event. Depending
        on the features used, this may include names, email addresses, phone
        numbers, team affiliations, roster details, jersey numbers, divisions,
        schedules, game assignments, score events, statistics, standings,
        waivers, eligibility status, registration status, and related notes.
      </p>

      <h3 style={{ marginTop: 20, fontSize: '1rem', fontWeight: 700 }}>Payment and billing information</h3>
      <p>
        Where payments are enabled, payment processing is handled by Stripe. We
        may store Stripe identifiers, transaction amounts, payment status,
        subscription status, connected account status, and related billing
        records. We do not store full credit card numbers.
      </p>

      <h3 style={{ marginTop: 20, fontSize: '1rem', fontWeight: 700 }}>Device, usage, and infrastructure information</h3>
      <p>
        Our hosting and infrastructure providers may process technical
        information such as IP address, browser type, device information,
        request URL, timestamps, and security logs. The app may also use browser
        storage for preferences, local drafts, theme settings, and league data
        saved locally before cloud sync is enabled.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>2. How we use information</h2>
      <ul className="m-bullets">
        <li>Provide, operate, secure, and improve The Scorekeeper.</li>
        <li>Authenticate users and control access to league and admin areas.</li>
        <li>Support registration, scheduling, rosters, scoring, standings, team pages, communications, analytics, and reporting.</li>
        <li>Generate AI-assisted or analytics-based outputs such as schedule recommendations, recaps, trends, reports, and operational insights.</li>
        <li>Process subscriptions, registration payments, payouts, refunds, and payment status updates where those features are enabled.</li>
        <li>Respond to contact requests, demo requests, support issues, and product feedback.</li>
        <li>Detect, prevent, and investigate abuse, fraud, security incidents, or violations of our Terms.</li>
        <li>Comply with legal obligations and enforce our agreements.</li>
      </ul>

      <p>
        We do not sell personal information. We do not use personal information
        for third-party advertising. We do not add you to a marketing list from
        a contact form submission unless you have asked us to follow up with you
        or the communication is otherwise related to your request.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>3. AI and analytics features</h2>
      <p>
        The Scorekeeper is designed to use league and event data to make the
        platform smarter. AI-assisted and analytics features may use information
        already in the platform to generate schedules, summaries, recaps,
        scouting notes, player trends, reports, and operational suggestions.
        These outputs are provided to help league operators make decisions, but
        they should be reviewed by a human before being relied on.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>4. How information is shared</h2>
      <p>We may share information in the following situations.</p>
      <ul className="m-bullets">
        <li>
          <strong>With league and tournament operators.</strong> Admins and
          authorized staff may see information needed to run their league,
          event, team, or assignment.
        </li>
        <li>
          <strong>With participants and the public.</strong> Schedules, rosters,
          scores, standings, statistics, team pages, and event pages may be
          visible to players, captains, families, fans, or the public depending
          on the league's settings and how the event is configured.
        </li>
        <li>
          <strong>With service providers.</strong> We use providers such as
          Cloudflare for hosting, storage, security, and edge infrastructure;
          Google for OAuth sign-in; and Stripe for payment processing where
          payments are enabled. These providers process information to help us
          deliver the service.
        </li>
        <li>
          <strong>For legal, safety, and security reasons.</strong> We may
          disclose information if required by law, legal process, or a valid
          government request, or when we believe disclosure is necessary to
          protect rights, safety, security, or prevent fraud and abuse.
        </li>
        <li>
          <strong>Business transfers.</strong> If we are involved in a merger,
          acquisition, financing, reorganization, bankruptcy, or sale of assets,
          information may be transferred as part of that transaction.
        </li>
      </ul>

      <h2 className="h2" style={{ marginTop: 32 }}>5. League operator responsibilities</h2>
      <p>
        League and tournament operators are responsible for having the right to
        enter, upload, publish, message, or otherwise use information about
        participants, staff, families, and teams. This includes responsibility
        for any required notices, consents, waivers, refund disclosures, media
        permissions, and youth participant permissions.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>6. Children and youth participants</h2>
      <p>
        The Scorekeeper is not directed to children under 13, and children under
        13 may not create their own accounts. Because roller hockey leagues may
        include youth participants, a parent, guardian, league operator, or
        other authorized adult may provide limited participant information as
        needed to run a league or event. If you believe we have collected
        personal information from a child under 13 without appropriate consent,
        contact us and we will take appropriate action.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>7. Cookies and browser storage</h2>
      <p>
        We use a first-party session cookie called <code>sk_session</code> to
        keep signed-in users authenticated. We may also use temporary cookies
        during the Google OAuth sign-in process. The app uses local browser
        storage for settings, drafts, role selection, local league data, and
        sync preferences. These technologies are not used for third-party
        advertising.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>8. Security and retention</h2>
      <p>
        We use reasonable administrative, technical, and organizational measures
        intended to protect information. No system is completely secure. We keep
        information for as long as reasonably necessary to provide the service,
        operate leagues and events, maintain business records, resolve disputes,
        enforce agreements, and comply with law. League data is generally kept
        for as long as the applicable league account or deployment remains
        active, unless deleted earlier by an authorized admin or by us.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>9. Your choices and rights</h2>
      <p>
        Depending on your location, you may have rights to access, correct,
        delete, or receive a copy of certain personal information, or to object
        to or limit certain processing. To make a request, contact us through
        the <Link to={paths.contact}>Contact page</Link>. We may need to verify your
        identity before responding.
      </p>
      <p>
        If your information was entered by a league or tournament operator, we
        may direct you to that operator or work with that operator to handle the
        request. We may also retain information where allowed or required for
        security, legal compliance, dispute resolution, accounting, or other
        legitimate purposes.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>10. Third-party links and services</h2>
      <p>
        The Scorekeeper may link to third-party services or send you to
        third-party pages, such as Google sign-in or Stripe checkout. Those
        services are governed by their own privacy policies and terms.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>11. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we will
        update the effective date. If we make material changes, we may provide
        additional notice where appropriate.
      </p>

      <h2 className="h2" style={{ marginTop: 32 }}>12. Contact</h2>
      <p>
        Questions about this Privacy Policy or our privacy practices can be sent
        through the <Link to={paths.contact}>Contact page</Link>.
      </p>
    </article>
  );
}
